import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {isEmpty} from "../../../../components/commonmethod/CommonMethod";
import {createDiligence} from "../../../../api/asset/DiligenceService";
import Th_title from "../../../../components/Th_title";
import {CodeSpecNum} from "../../../../constant/CodeSpecNum";
import ReadOnlyInputValue from "../../../../components/input/ReadOnlyInputValue";
import {onCreateClickListener} from "../../../../components/commonmethod/simmsDrawerMethod";
import SearchSelector from "../../../../components/searchSelector/SearchSelector";
import UserSelectionField from "../../../../components/selection/UserSelectionField";
import {AssetSearchFilters} from "../info/shared/AssetSearchFilters";
import SimmsInputSearch from "../../../../components/input/SimmsInputSearch";
import Selector from "../../../../components/selector/Selector";
import SimmsDatePicker from "../../../../components/datepicker/SimmsDatePicker";
import {dateOnChange} from "../../../../components/commonmethod/simmsDateMethod";
import dayjs from "dayjs";
import SimmsInput from "../../../../components/input/SimmsInput";
import {inputOnChange} from "../../../../components/commonmethod/simmsInputMethod";
import SimmsTextArea from "../../../../components/input/SimmsTextArea";
import {
    ContentDetailTwoColLayout,
    DetailFilePanel,
    DetailFormPageShell,
    DetailKvReadOnly,
    DetailKvTable,
    DetailListPage,
    DetailPanel,
    DetailSummaryButton,
} from "../../../../components/detailLayout";
import "../../../../components/detailLayout/detailListLayout.css";
import "../info/subPages/view/assetInfo.css";
import {getApprovalProcessSubmitPayload} from "../../../settings/approval/shared/approvalProcessStore";

function resolveEntityId(entity) {
    return entity?.id ?? entity?.userId ?? entity?.subId ?? null;
}

function hasEntityRef(entity) {
    if (entity == null) return false;
    if (typeof entity !== "object") return !isEmpty(entity);
    const id = resolveEntityId(entity);
    if (id != null && id !== "") return true;
    const label = entity.name ?? entity.assetName ?? entity.title ?? "";
    return String(label).trim() !== "";
}

function getMissingRequiredFieldLabels(diligenceState) {
    const missing = [];
    if (isEmpty(diligenceState.title)) missing.push("실사명");
    if (!hasEntityRef(diligenceState.requestedBy)) missing.push("요청자");
    if (isEmpty(diligenceState.reqStartDate)) missing.push("실사기간(시작)");
    if (isEmpty(diligenceState.reqEndDate)) missing.push("실사기간(종료)");
    if (!Array.isArray(diligenceState.handler) || diligenceState.handler.length === 0) {
        missing.push("담당자");
    }
    if (!Array.isArray(diligenceState.assets) || diligenceState.assets.length === 0) {
        missing.push("자산목록");
    }
    return missing;
}

function DiligenceCreate() {
    const platFrom = useSelector((state) => state.auth.platFrom);
    const user = useSelector((state) => state.auth.user);
    const [diligence, setDiligence] = useState({
        handler: [],
        assets: [],
    });
    const [docs, setDocs] = useState([]);
    const currentDate = new Date();
    const navigate = useNavigate();

    const requires = ["title", "handler", "requestedBy", "assets", "reqStartDate", "reqEndDate"];

    const create = async () => {
        const missing = getMissingRequiredFieldLabels(diligence);
        if (missing.length > 0) {
            alert(`필수값이 누락되었습니다.\n(${missing.join(", ")})`);
            return;
        }
        const {required_validate: _requiredValidate, ...diligencePayload} = diligence;
        const data = {
            ...diligencePayload,
            ...getApprovalProcessSubmitPayload(),
        };

        const docFile = docs.map((each) => each.object);

        try {
            const response = await createDiligence(data, docFile);
            if (response.status === 200) {
                handleActiveToast({message: "등록되었습니다."});
                navigate(`/diligence/${response.data.id}`, {state: {id: response.data.id}});
            } else if (response.status === 201 || response.status === 202) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            }
        } catch {
            handleActiveError({message: "등록에 실패했습니다."});
        }
    };

    const diligenceAssetList = diligence?.assets?.map((e) => e.asset).filter(Boolean) ?? [];

    return (
        <DetailFormPageShell
            className="common-container container-height-full border-l-gray create-page detail-list-layout asset-detail-list-layout"
            title="실사등록"
            meta="신규 실사 정보를 입력하세요"
            actions={
                <>
                    <DetailSummaryButton muted onClick={() => navigate("/diligence")}>
                        취소
                    </DetailSummaryButton>
                    <DetailSummaryButton
                        onClick={() => {
                            if (window.confirm("등록 하시겠습니까?")) {
                                create();
                            }
                        }}
                    >
                        등록
                    </DetailSummaryButton>
                </>
            }
        >
            <DetailListPage isEditing className="flex min-h-0 flex-1 flex-col">
                <div className="container-height-body asset-detail-body detail-list-body flex min-h-0 flex-1 flex-col overflow-hidden">
                    <ContentDetailTwoColLayout
                        main={
                        <DetailPanel title="기본정보">
                            <DetailKvTable>
                                <tbody>
                                    <tr>
                                        <DetailKvReadOnly
                                            label="등록일시"
                                            value={currentDate.toISOString()}
                                            type="date"
                                        />
                                        <DetailKvReadOnly label="수정일시" value="" />
                                    </tr>
                                    <tr>
                                        <DetailKvReadOnly label="등록자" value={user.name} />
                                        <DetailKvReadOnly label="수정자" value="" />
                                    </tr>
                                    <tr>
                                        <Th_title
                                            title="실사명"
                                            colspan={1}
                                            required={requires.includes("title")}
                                            className="fieldset-td fieldset-td-input"
                                            auth
                                        >
                                            <SimmsInput
                                                onChange={(v) => inputOnChange(setDiligence, "title", v, true)}
                                                data={diligence.title}
                                            />
                                        </Th_title>
                                        <Th_title
                                            title="실사번호"
                                            colspan={1}
                                            className="fieldset-td fieldset-td-input"
                                            auth
                                        >
                                            <ReadOnlyInputValue
                                                target="tickets"
                                                data={{data: diligence, setData: setDiligence}}
                                                value={new Date().getTime()}
                                            />
                                        </Th_title>
                                    </tr>
                                    <tr>
                                        <Th_title
                                            title="요청자"
                                            colspan={1}
                                            className="fieldset-td fieldset-td-input"
                                            required={requires.includes("requestedBy")}
                                            auth
                                        >
                                            <Selector
                                                onChange={(v) =>
                                                    setDiligence((prev) => {
                                                        const next = {...prev};
                                                        if (v == null) {
                                                            delete next.requestedBy;
                                                        } else if (
                                                            resolveEntityId(prev.requestedBy) != null &&
                                                            String(resolveEntityId(prev.requestedBy)) ===
                                                                String(resolveEntityId(v))
                                                        ) {
                                                            delete next.requestedBy;
                                                        } else {
                                                            next.requestedBy = v;
                                                        }
                                                        return next;
                                                    })
                                                }
                                                data={diligence.requestedBy}
                                                entity="userAccount"
                                                uniqueKey="requestedBy"
                                            >
                                                <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                                            </Selector>
                                        </Th_title>
                                        <Th_title
                                            title="실사기간"
                                            colspan={1}
                                            className="fieldset-td fieldset-td-input"
                                            required
                                            auth
                                        >
                                            <div className="web-flex asset-detail-date-range">
                                                <SimmsDatePicker
                                                    onChange={(v) =>
                                                        dateOnChange(setDiligence, "reqStartDate", v, true)
                                                    }
                                                    data={diligence.reqStartDate}
                                                    constraint={{
                                                        required: requires.includes("reqStartDate"),
                                                        lessThan:
                                                            diligence.reqEndDate && dayjs(diligence.reqEndDate),
                                                    }}
                                                    auth
                                                />
                                                <span className="asset-detail-date-range-sep">~</span>
                                                <SimmsDatePicker
                                                    onChange={(v) =>
                                                        dateOnChange(setDiligence, "reqEndDate", v, true)
                                                    }
                                                    data={diligence.reqEndDate}
                                                    constraint={{
                                                        required: requires.includes("reqEndDate"),
                                                        largerThan:
                                                            diligence.reqStartDate && dayjs(diligence.reqStartDate),
                                                    }}
                                                    auth
                                                />
                                            </div>
                                        </Th_title>
                                    </tr>
                                    <tr>
                                        <Th_title
                                            title="실사종류"
                                            colspan={3}
                                            className="fieldset-td fieldset-td-input"
                                            auth
                                        >
                                            <Selector
                                                onChange={(v) =>
                                                    onCreateClickListener(setDiligence, "code", diligence.code, v)
                                                }
                                                data={diligence.code}
                                                entity="code"
                                                parentKey={CodeSpecNum.DiligenceCode}
                                                recursive
                                            />
                                        </Th_title>
                                    </tr>
                                    <tr>
                                        <Th_title
                                            title="담당자"
                                            colspan={1}
                                            className="fieldset-td fieldset-td-input relative align-top"
                                            required={requires.includes("handler")}
                                            auth
                                        >
                                            <UserSelectionField
                                                layout="inline"
                                                data={diligence.handler}
                                                onRemove={(v) =>
                                                    setDiligence((prev) => ({
                                                        ...prev,
                                                        handler: (prev.handler ?? []).filter((h) => h.id !== v.id),
                                                    }))
                                                }
                                            >
                                                <SearchSelector
                                                    auth
                                                    multiSelect
                                                    variant="compact"
                                                    entity="userAccount"
                                                    uniqueKey="handler"
                                                    data={diligence.handler}
                                                    placeHolder="선택"
                                                    onChange={(v) => {
                                                        if (v?.__multiFlat) {
                                                            setDiligence((prev) => ({
                                                                ...prev,
                                                                handler: v.__multiFlat,
                                                            }));
                                                        }
                                                    }}
                                                >
                                                    <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                                                </SearchSelector>
                                            </UserSelectionField>
                                        </Th_title>
                                        <Th_title
                                            title="자산목록"
                                            colspan={1}
                                            className="fieldset-td relative align-top"
                                            required={requires.includes("assets")}
                                            auth
                                        >
                                            <UserSelectionField
                                                layout="inline"
                                                data={diligenceAssetList}
                                                countUnit="개"
                                                chipEntity="asset-diligence"
                                                onRemove={(v) =>
                                                    setDiligence((prev) => ({
                                                        ...prev,
                                                        assets: (prev.assets ?? []).filter(
                                                            (item) => item.asset?.id !== v.id,
                                                        ),
                                                    }))
                                                }
                                            >
                                                <SearchSelector
                                                    auth
                                                    multiSelect
                                                    variant="compact"
                                                    entity="asset-diligence"
                                                    uniqueKey="assets"
                                                    data={diligenceAssetList}
                                                    placeHolder="선택"
                                                    onChange={(v) => {
                                                        if (v?.__multiFlat) {
                                                            setDiligence((prev) => ({
                                                                ...prev,
                                                                assets: v.__multiFlat.map((asset) => ({asset})),
                                                            }));
                                                        }
                                                    }}
                                                >
                                                    <AssetSearchFilters platFrom={platFrom} />
                                                </SearchSelector>
                                            </UserSelectionField>
                                        </Th_title>
                                    </tr>
                                    <tr>
                                        <Th_title title="실사내용" colspan={3} className="fieldset-td" auth>
                                            <SimmsTextArea
                                                onChange={(v) => inputOnChange(setDiligence, "etc", v)}
                                                data={diligence.etc}
                                                style={{width: "100%", minHeight: "80px"}}
                                            />
                                        </Th_title>
                                    </tr>
                                </tbody>
                            </DetailKvTable>
                        </DetailPanel>
                        }
                        aside={
                        <DetailFilePanel
                            title="첨부"
                            isEditing
                            pendingFiles={{files: docs, setFiles: setDocs}}
                            pendingInputId="diligence_create_doc"
                        />
                        }
                    />
                </div>
            </DetailListPage>
        </DetailFormPageShell>
    );
}

export default DiligenceCreate;
